import React, { useState, useEffect } from 'react';
import { get } from 'lodash';
import { Field, useForm, useFormState } from 'react-final-form';
import { DAYS_OF_THE_WEEK, getOverlaps, timeSlotsOptions } from '@unite-us/client-utils';
import { SimpleSelectField, CheckBoxField } from '@unite-us/app-components';
import ButtonAddField from 'src/components/Organization/components/ButtonAddField';
import ButtonRemoveField from 'src/components/Organization/components/ButtonRemoveField';

const ContactHoursEdit = () => {
  const [isChecked, setIsChecked] = useState({});
  const [overlaps, setOverlaps] = useState({});
  const { values } = useFormState({ subscription: { values: true } });
  const form = useForm();

  const getTimeslots = (lowercaseDay) => get(values, `contact_preferences.timeslots.${lowercaseDay}`, []);

  // Initialize isChecked from existing form values when they load
  useEffect(() => {
    const initialChecked = {};
    DAYS_OF_THE_WEEK.forEach((day) => {
      const lowercaseDay = day.toLowerCase();
      const timeslots = getTimeslots(lowercaseDay);
      if (timeslots && timeslots.length > 0) {
        initialChecked[lowercaseDay] = true;
      }
    });
    setIsChecked(initialChecked);
  }, [values.contact_preferences?.timeslots]); // Re-run when timeslots change

  const validateOverlaps = (lowercaseDay) => {
    const dayTimeslots = getTimeslots(lowercaseDay);
    // Update checkbox state when adding timeslot
    setIsChecked({ ...isChecked, [lowercaseDay]: true });
    const dayOverlaps = getOverlaps(dayTimeslots);
    const newOverlaps = { ...overlaps };
    newOverlaps[lowercaseDay] = dayOverlaps;
    setOverlaps(newOverlaps);
  };

  const handleAddField = (lowercaseDay) => {
    const timeslots = getTimeslots(lowercaseDay);
    if (timeslots.length === 0) {
      form.mutators.push(`contact_preferences.timeslots.${lowercaseDay}`, { start_time: '09:00', end_time: '17:00' });
    } else {
      form.mutators.push(`contact_preferences.timeslots.${lowercaseDay}`, { start_time: null, end_time: null });
    }
    setIsChecked({ ...isChecked, [lowercaseDay]: true });
  };

  const handleRemoveField = (lowercaseDay, index) => {
    form.mutators.remove(`contact_preferences.timeslots.${lowercaseDay}`, index);

    setTimeout(() => {
      const timeslots = getTimeslots(lowercaseDay);
      if (index === 0 && timeslots.length === 0) {
        setIsChecked({ ...isChecked, [lowercaseDay]: false });
      }
      validateOverlaps(lowercaseDay);
    }, 0);
  };

  const handleDayCheckbox = (lowercaseDay) => {
    const timeslots = getTimeslots(lowercaseDay);
    // Remove all timeslots for this day
    for (let i = timeslots.length - 1; i >= 0; i--) {
      form.mutators.remove(`contact_preferences.timeslots.${lowercaseDay}`, i);
    }
    if (!isChecked[lowercaseDay]) {
      handleAddField(lowercaseDay);
    }
    setIsChecked({ ...isChecked, [lowercaseDay]: !isChecked[lowercaseDay] });
  };

  const validateStartTime = (value, lowercaseDay, index) => {
    if (isChecked[lowercaseDay] && !value) {
      return 'Required';
    }
    if (overlaps[lowercaseDay]?.[index]) {
      return 'Times cannot overlap';
    }
    return undefined;
  };

  const validateEndTime = (value, lowercaseDay, index) => {
    if (isChecked[lowercaseDay] && !value) {
      return 'Required';
    }
    if (overlaps[lowercaseDay]?.[index]) {
      return ' '; // whitespace sets error style
    }
    return undefined;
  };

  const onChangeStartTime = (lowercaseDay, index, value) => {
    const dayTimeslots = getTimeslots(lowercaseDay);
    const endTimeValue = get(dayTimeslots, `[${index}].end_time`);

    // If start_time value changes to a time later than end_time value, clear the end_time value.
    if (endTimeValue && endTimeValue <= value) {
      form.change(`contact_preferences.timeslots.${lowercaseDay}[${index}].end_time`, null);
    }
    validateOverlaps(lowercaseDay);
  };

  return (
    <div className="contact-hours-edit">
      {
        DAYS_OF_THE_WEEK.map((day) => {
          const lowercaseDay = day.toLowerCase();
          const dayTimeslots = getTimeslots(lowercaseDay);

          return (
            <div
              key={`contact-hours-edit-${lowercaseDay}`}
              className="grid grid-cols-5"
            >
              <div className="pt-2 h-14">
                <CheckBoxField
                  className="pt-2 h-14"
                  checked={isChecked[lowercaseDay]}
                  id={`contact-hours-edit-checked-${lowercaseDay}`}
                  inline
                  label={day}
                  labelKey="display_name"
                  valueKey="lowercaseDay"
                  onChange={() => handleDayCheckbox(lowercaseDay)}
                  showHint={false}
                  showError={false}
                />
              </div>

              {dayTimeslots?.map((dayTimeslot, index) => {
                const startTime = dayTimeslot.start_time || '';
                const endTime = dayTimeslot.end_time || '';
                const timeslotKey = `${lowercaseDay}-${index}-${startTime}-${endTime}`;
                return (
                  <React.Fragment key={timeslotKey}>
                    {isChecked[lowercaseDay] && (
                      <div
                        className="flex gap-2 px-0 col-span-3 justify-start"
                        data-testid="contact-hours-edit-row"
                      >
                        <Field
                          name={`contact_preferences.timeslots.${lowercaseDay}[${index}].start_time`}
                          validate={(value) => validateStartTime(value, lowercaseDay, index)}
                        >
                          {({ input, meta }) => (
                            <SimpleSelectField
                              className="w-32"
                              clearable={false}
                              id={`contact-hours-edit-start-${lowercaseDay}-${index}`}
                              value={input.value}
                              onChange={(value) => {
                                input.onChange(value);
                                onChangeStartTime(lowercaseDay, index, value);
                              }}
                              onBlur={input.onBlur}
                              options={timeSlotsOptions()}
                              placeholder="Start"
                              required
                              error={meta.touched && meta.error}
                              searchable
                            />
                          )}
                        </Field>

                        <div className="pt-2 w-10">to</div>

                        <Field
                          name={`contact_preferences.timeslots.${lowercaseDay}[${index}].end_time`}
                          validate={(value) => validateEndTime(value, lowercaseDay, index)}
                        >
                          {({ input, meta }) => (
                            <SimpleSelectField
                              className="w-32"
                              clearable={false}
                              id={`contact-hours-edit-end-${lowercaseDay}-${index}`}
                              value={input.value}
                              onChange={(value) => {
                                input.onChange(value);
                                validateOverlaps(lowercaseDay);
                              }}
                              onBlur={input.onBlur}
                              options={timeSlotsOptions(get(dayTimeslot, 'start_time'))}
                              placeholder="End"
                              required
                              error={meta.touched && meta.error}
                              searchable
                            />
                          )}
                        </Field>

                        <div className="pt-1">
                          <ButtonRemoveField
                            onClick={() => handleRemoveField(lowercaseDay, index)}
                            testId={`contact-hours-edit-remove-${lowercaseDay}-${index}`}
                          />
                        </div>
                      </div>
                    )}
                    {index > 0 && <div className="w-32" />}
                  </React.Fragment>
                );
              })}

              {
                isChecked[lowercaseDay] ? (
                  <div className="pt-2 justify-self-end px-0 row-start-1 col-start-5">
                    <ButtonAddField
                      label="Add Timeslot"
                      onClick={() => handleAddField(lowercaseDay)}
                    />
                  </div>
                ) :
                  <div className="pt-2 font-regular-font">Do not contact</div>
              }
            </div>
          );
        })
      }
    </div>
  );
};

ContactHoursEdit.propTypes = {};

export default ContactHoursEdit;
